import { Component, Input, Output, EventEmitter, forwardRef, ViewChild, ElementRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-custom-ng-select',
  templateUrl: './custom-ng-select.component.html',
  styleUrls: ['./custom-ng-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomNgSelectComponent),
      multi: true
    }
  ]
})
export class CustomNgSelectComponent implements ControlValueAccessor {

  @ViewChild('searchInput') searchInput!: ElementRef;

  // Configuración del select
  @Input() items: any[] = [];
  @Input() bindLabel: string = 'nombre';
  @Input() bindValue: string = 'id';
  @Input() placeholder: string = 'Seleccione...';
  @Input() disabled: boolean = false;
  @Input() searchable: boolean = true;
  @Input() clearable: boolean = true;

  // Template personalizado (opcional)
  @Input() showSecondaryText: boolean = false;
  @Input() secondaryTextField: string = 'descripcion';
  @Input() iconClass: string = 'ri-information-line';

  // Loading (puede ser controlado externamente)
  @Input() loading: boolean = false;

  // Estado de validación
  @Input() isInvalid: boolean = false;

  // Loading con búsqueda en backend
  @Input() searchInBackend: boolean = false;
  @Input() minSearchLength: number = 3;
  @Input() searchDebounceTime: number = 400;

  // Eventos
  @Output() search = new EventEmitter<string>();
  @Output() change = new EventEmitter<any>();
  @Output() clear = new EventEmitter<void>();

  // Control interno
  selectedValue: any = null;
  searchTerm$ = new Subject<string>();

  // Función de comparación para ng-select
  compareWith = (item: any, selected: any) => {
    if (!item || !selected) return false;

    // Si bindValue está definido, comparar por ese campo
    if (this.bindValue) {
      // Si ambos son objetos, comparar el campo bindValue
      if (typeof item === 'object' && typeof selected === 'object') {
        // tslint:disable-next-line:triple-equals
        return item[this.bindValue] == selected[this.bindValue];
      }
      // Si uno es primitivo y otro objeto, comparar el primitivo con el campo del objeto
      if (typeof item === 'object') {
        // tslint:disable-next-line:triple-equals
        return item[this.bindValue] == selected;
      }
      if (typeof selected === 'object') {
        // tslint:disable-next-line:triple-equals
        return item == selected[this.bindValue];
      }
      // Si ambos son primitivos, comparar con == para tolerar string vs number
      // tslint:disable-next-line:triple-equals
      return item == selected;
    }

    // Sin bindValue, comparar con == para tolerar string vs number
    // tslint:disable-next-line:triple-equals
    return item == selected;
  };

  // ControlValueAccessor
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    // Configurar búsqueda con debounce
    this.searchTerm$.pipe(
      debounceTime(this.searchDebounceTime),
      distinctUntilChanged()
    ).subscribe(searchTerm => {
      if (this.searchInBackend && searchTerm && searchTerm.length >= this.minSearchLength) {
        this.search.emit(searchTerm);
      }
    });
  }

  /**
   * Cuando se busca (se escribe en el select)
   */
  onSearch(event: { term: string, items: any[] }) {
    if (this.searchInBackend) {
      this.searchTerm$.next(event.term);
    }
  }

  /**
   * Cuando se selecciona un item
   */
  onChangeSelection(event: any) {
    // Si el evento es un objeto y tenemos bindValue, extraer solo el valor
    let valueToEmit = event;

    if (event && typeof event === 'object' && this.bindValue) {
      valueToEmit = event[this.bindValue];
    }

    this.selectedValue = valueToEmit;
    this.onChange(valueToEmit);
    this.onTouched();
    this.change.emit(event);
  }

  /**
   * Cuando se limpia la selección
   */
  onClear() {
    this.selectedValue = null;
    this.onChange(null);
    this.onTouched();
    this.clear.emit();
  }


  // ControlValueAccessor implementation
  writeValue(value: any): void {
    // Simplemente establecer el valor tal cual viene
    // ng-select se encargará de encontrar el objeto correspondiente
    this.selectedValue = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
